import type { InvoiceResponse, Price } from '@cvo/domain';

export const useOutstandingBalanceStore = defineStore('outstandingBalance', () => {
  const api = useApi();

  // ===== STATE ===== //

  const unpaidInvoices = ref<InvoiceResponse[]>([]);
  const totalDue = ref<Price | null>(null);
  const isProcessingRetry = ref<boolean>(false);
  const retryProgressStep = ref<1 | 2>(1);

  const fetchBalanceRequest = useAsyncRequest(fetchBalance);
  const retryPaymentRequest = useAsyncRequest(retryPayment);

  // ===== COMPUTED ===== //

  /** True when there is at least one unpaid invoice. */
  const hasOutstandingBalance = computed<boolean>(() => unpaidInvoices.value.length > 0);

  // ===== ACTIONS ===== //

  /**
   * Fetch outstanding balance and populate unpaid invoices from the response.
   */
  async function fetchBalance(): Promise<void> {
    await api.billing.getBalance().then((response) => {
      unpaidInvoices.value = response.data.unpaidInvoices;
      totalDue.value = response.data.unpaidInvoices.length > 0 ? response.data.balance : null;
    });
  }

  /**
   * Trigger a payment retry for all unpaid payments of the authenticated
   * subscriber's billing account.
   */
  async function retryPayment(): Promise<void> {
    await api.billing.retryPayment();
  }

  /** Reset outstanding balance state. */
  function reset(): void {
    unpaidInvoices.value = [];
    totalDue.value = null;
    isProcessingRetry.value = false;
    retryProgressStep.value = 1;
    fetchBalanceRequest.clear();
  }

  return {
    // State
    unpaidInvoices,
    isProcessingRetry,
    retryProgressStep,

    // Computed
    totalDue,
    hasOutstandingBalance,

    // Async request state
    isLoading: fetchBalanceRequest.isLoading,
    error: fetchBalanceRequest.error,
    retryError: retryPaymentRequest.error,

    // Actions
    fetchBalance: fetchBalanceRequest.run,
    retryPayment: retryPaymentRequest.run,
    reset,
  };
});
